import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Card, Form, Result, message } from 'antd';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { Formik } from 'formik';
import { FormikForm } from 'client/ui/form/FormikForm';
import { TextInputItem } from 'client/ui/form/input/TextInputItem';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import {
  useThemeConfig,
  useThemeLabel
} from 'client/components/schema/config/ClientConfigModule';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { CheckboxInputItem } from 'client/ui/form/input/CheckboxInputItem';
import { FormDividerSpace } from 'client/ui/space/FormDividerSpace';
import { DatePickerInputItem } from 'client/ui/form/input/DatePickerInputItem';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { CitizenApi } from 'client/components/schema/citizen/CitizenApi';
import {
  citizenEditValidator,
  citizenRegisterValidator,
  ICitizenEditDto,
  ICitizenRegisterDto
} from 'common/dto/CitizenDto';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { logger } from 'client/core/logger/logger';
import { useTranslation } from 'react-i18next';
import { resolvePublicPath } from 'client/core/appConfig';
import { UserProfileFieldsForm } from 'client/components/schema/citizen/form/UserProfileFieldsForm';
import {
  AuthActions,
  useCurrentCitizen
} from 'client/components/auth/AuthModule';
import { useDispatch } from 'react-redux';
import { UserNationalityForm } from 'client/components/schema/citizen/form/UserNationalityForm';

interface Params {
  id: string;
}

export interface UserProfilePageProps extends RouteComponentProps<Params> {}

/**
 * Pagina per la modifica del profilo utente (senza SPID)
 */
export function UserProfilePage(props: UserProfilePageProps) {
  const config = useThemeConfig();
  const { t } = useTranslation();
  const citizen = useCurrentCitizen();
  const dispatch = useDispatch();

  useBreadcrumbItem({
    path: '/',
    title: t('procedures.breadcrumb', 'Istanze')
  });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: t('user.profile.breadcrumb', 'Profilo Utente')
  });

  const [update, updateState] = useApiMutation(CitizenApi.update, {});

  if (!citizen) return null;

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 12 }
  };
  const tailLayout = {
    wrapperCol: { sm: { offset: 6, span: 12 }, xs: { offset: 0, span: 24 } }
  };
  return (
    <FrontLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card>
          <Formik<ICitizenEditDto>
            enableReinitialize
            initialValues={
              updateState.response?.data.citizen ?? (citizen as any)
            }
            validate={schemaToValidator(citizenEditValidator)}
            validateOnChange={false}
            onSubmit={async rawValues => {
              try {
                const values = await citizenEditValidator.validate(rawValues, {
                  abortEarly: false
                });
                await update({
                  data: { input: { ...values } }
                });
                message.success(
                  t(
                    'user.profile.response.success.title',
                    'Anagrafica aggiornata con successo'
                  )
                );
                dispatch(AuthActions.reload());
              } catch (e) {
                logger.error('Non è stato possibile aggiornare il profilo.', e);
                message.error(
                  t(
                    'user.profile.response.error.message',
                    'Si è verificato un errore durante la registrazione'
                  )
                );
              }
            }}
          >
            {({ isSubmitting, dirty }) => (
              <>
                <PreventNavigation
                  when={dirty && !isSubmitting}
                  message={t(
                    'user.registration.prevent-navigation-message',
                    `Ci sono dati non salvati, abbandonare la pagina? (Tutte le modifiche non salvate andranno perse)`
                  )}
                />

                <FormikForm {...layout}>
                  <UserNationalityForm layout={layout} />

                  {/* Form Utente */}
                  <FormDividerSpace
                    title={t(
                      'user.registration.form.top-title',
                      'Dati di accesso'
                    )}
                    layout={layout}
                    style={{ marginTop: 0 }}
                  />

                  <TextInputItem
                    prefix={<UserOutlined style={{ color: '#d9d9d9' }} />}
                    name="email"
                    disabled
                    label="Email"
                  />

                  <UserProfileFieldsForm />

                  <Form.Item {...tailLayout}>
                    <Button
                      loading={isSubmitting}
                      htmlType="submit"
                      type="primary"
                      style={{ width: '100%' }}
                    >
                      {t('user.profile.form.submit-button', 'Salva Modifiche')}
                    </Button>
                  </Form.Item>
                </FormikForm>
              </>
            )}
          </Formik>
        </Card>
      </div>
    </FrontLayout>
  );
}
